/**
 * CreateEventRegistration — Step: Registration settings.
 * Verbatim port of core settings (window, approval, guests, consent, confirm,
 * terms, standard attendee fields). EventQuestionsConfig and EventPromosConfig
 * are simplified stubs — full library pickers port in a follow-on drop.
 */

import { useEffect, useRef, useState, type ChangeEvent, type MouseEvent } from "react";
import { Btn, Card, DatePicker, Icon, Input, Pill, T, Toggle, tx } from "@v0y4ge/jrni-ui";
import { parseDateString, formatDateString } from "@v0y4ge/jrni-ui";
import { CreationShell } from "./CreationShell";
import { checkUrl, UrlField } from "./url-field";
import { CreationStore, useCreationConfig } from "./store";
import { FormSection, WizardCanvas } from "./wizard-utils";
import { NumberField } from "../../../lib/NumberField";
import { useQuestions } from "../../questions/useQuestions";
import { useSurveys } from "../../surveys/SurveyEditorPage";
import { useT } from "../../../lib/i18n";

// ── Helpers ───────────────────────────────────────────────────────────────────
const INPUT = { width: "100%", height: 40, padding: "0 12px", border: `1px solid ${T.n4}`, borderRadius: 4, background: T.white, fontSize: 14, fontFamily: T.font, color: T.n1, outline: "none" } as const;
const labelSt = { fontSize: 13, color: T.n1, fontWeight: 500, display: "block", marginBottom: 4 } as const;

function SubHead({ title, hint }: { title: string; hint?: string }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: T.n1 }}>{title}</div>
      {hint ? <div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>{hint}</div> : null}
    </div>
  );
}

function ToggleRow({ label, sub, on, onChange, disabled }: { label: string; sub?: string; on: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 0", borderBottom: `1px solid ${T.border}` }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13.5, color: T.n1, fontWeight: 500 }}>{label}</div>
        {sub ? <div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>{sub}</div> : null}
      </div>
      <Toggle on={on} onChange={disabled ? undefined : onChange} disabled={disabled} />
    </div>
  );
}

function PresetPill({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <Btn onClick={onClick} aria-pressed={active} style={{ height: 26, padding: active ? "0 9px 0 7px" : "0 9px", borderRadius: 999, border: `1px solid ${active ? T.p1 : T.n4}`, background: active ? T.p1 : T.white, color: active ? T.white : T.n1, fontFamily: T.font, fontSize: 11.5, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4, transition: "background 120ms, color 120ms" }}>
      {active ? <Icon.check size={10} /> : null}{label}
    </Btn>
  );
}

// ── Rich terms field: plain text + highlight-to-insert-link ────────────────────
function normalizeHref(raw: string): string {
  const url = raw.trim();
  if (!url) return "";
  if (/^(mailto:|tel:)/i.test(url)) return url;
  if (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(url)) return "mailto:" + url;
  // Web links run through the shared checker (url-field.tsx) so "teams" or a
  // pasted line break can't ship as an href.
  const res = checkUrl(url);
  return res.state === "valid" ? res.normalized : "";
}

/** mail/phone shorthand or a valid http(s) link — what Apply will accept. */
function hrefIsUsable(raw: string): boolean {
  return !!normalizeHref(raw);
}

function TermsRichField({ value, onChange }: { value: string; onChange: (html: string) => void }) {
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  const savedRange = useRef<Range | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [selText, setSelText] = useState("");
  const [focused, setFocused] = useState(false);

  // Seed the editable content once (contentEditable is uncontrolled to keep the caret stable).
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== value) ref.current.innerHTML = value || "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const styleAnchors = () => {
    ref.current?.querySelectorAll("a").forEach((a) => {
      a.style.color = T.p1;
      a.style.textDecoration = "underline";
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener noreferrer");
    });
  };
  const emit = () => { if (ref.current) { styleAnchors(); onChange(ref.current.innerHTML); } };

  const anchorAt = (node: Node | null): HTMLAnchorElement | null => {
    let n: Node | null = node;
    while (n && n !== ref.current) { if (n instanceof HTMLAnchorElement) return n; n = n.parentNode; }
    return null;
  };

  const openLink = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && ref.current && ref.current.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
      const existing = anchorAt(sel.anchorNode);
      setSelText(existing ? existing.textContent || "" : sel.toString());
      setLinkUrl(existing ? existing.getAttribute("href") || "" : "");
    } else {
      savedRange.current = null;
      setSelText("");
      setLinkUrl("");
    }
    setLinkOpen(true);
  };

  const restoreSel = () => {
    const sel = window.getSelection();
    if (sel && savedRange.current) { sel.removeAllRanges(); sel.addRange(savedRange.current); }
  };

  const applyLink = () => {
    const href = normalizeHref(linkUrl);
    if (!linkUrl.trim()) { setLinkOpen(false); return; }
    if (!href) return; // invalid — the inline notice below says why
    ref.current?.focus();
    restoreSel();
    const sel = window.getSelection();
    if (sel && sel.isCollapsed) {
      const label = (selText || linkUrl).trim() || href;
      document.execCommand("insertHTML", false, `<a href="${href}">${label}</a>&nbsp;`);
    } else {
      document.execCommand("createLink", false, href);
    }
    emit();
    setLinkOpen(false);
    setLinkUrl("");
    setSelText("");
  };

  const removeLink = () => {
    ref.current?.focus();
    restoreSel();
    document.execCommand("unlink");
    emit();
    setLinkOpen(false);
  };

  const barBtn = { display: "inline-flex", alignItems: "center", gap: 6, height: 28, padding: "0 10px", border: `1px solid ${T.n4}`, borderRadius: 5, background: T.white, color: T.n1, fontFamily: T.font, fontSize: 12, fontWeight: 500, cursor: "pointer" } as const;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <Btn type="button" onMouseDown={(e: MouseEvent<HTMLButtonElement>) => e.preventDefault()} onClick={openLink} style={barBtn}>
          <Icon.link size={13} /> {t("events.creationCreateEventRegistration.insertLink", "Insert link")}</Btn>
        <span style={{ fontSize: 11.5, color: T.soft }}>{t("events.creationCreateEventRegistration.selectTextThenInsertALink", "Select text, then insert a link.")}</span>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{ ...INPUT, height: "auto", minHeight: 80, padding: 12, lineHeight: 1.55, outline: "none", border: `1px solid ${focused ? T.p1 : T.n4}`, cursor: "text", overflowWrap: "anywhere" }}
      />
      {linkOpen ? (() => {
        const badLink = !!linkUrl.trim() && !hrefIsUsable(linkUrl);
        return (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              autoFocus
              value={linkUrl}
              type="url"
              spellCheck={false}
              aria-invalid={badLink || undefined}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyLink(); } if (e.key === "Escape") setLinkOpen(false); }}
              placeholder={t("events.creationCreateEventRegistration.httpsExampleCom", "https://example.com")}
              style={{ ...INPUT, height: 34, flex: 1, border: `1px solid ${badLink ? T.red1 : T.n4}` }}
            />
            <Btn size="sm" disabled={badLink} onClick={applyLink}>{t("events.creationCreateEventRegistration.apply", "Apply")}</Btn>
            {linkUrl ? <Btn size="sm" variant="secondary" onClick={removeLink}>{t("common.actions.remove", "Remove")}</Btn> : null}
            <Btn size="sm" variant="ghost" onClick={() => setLinkOpen(false)}>{t("common.actions.cancel", "Cancel")}</Btn>
          </div>
          {badLink ? (
            <div role="alert" style={{ display: "flex", gap: 6, alignItems: "flex-start", fontSize: 12, color: T.red1, fontWeight: 500, marginTop: 5 }}>
              <span style={{ flexShrink: 0, marginTop: 1, display: "inline-flex" }}><Icon.warn size={12} /></span>
              <span>{t("events.creationUrlField.notAValidLink", "That isn't a valid link.")}{" "}{t("events.creationUrlField.forExample", "For example:")} https://example.com/terms</span>
            </div>
          ) : null}
        </div>
        );
      })() : null}
    </div>
  );
}

const STD_FIELDS = [
  { id: "firstName", label: "First name", labelKey: "events.creationCreateEventRegistration.firstName", locked: true },
  { id: "lastName", label: "Last name", labelKey: "events.creationCreateEventRegistration.lastName", locked: true },
  { id: "email", label: "Email address", labelKey: "events.creationCreateEventRegistration.emailAddress", locked: true },
  { id: "phone", label: "Phone number", labelKey: "events.creationCreateEventRegistration.phoneNumber", locked: false },
  { id: "homeAddress", label: "Home address", labelKey: "events.creationCreateEventRegistration.homeAddress", locked: false },
  { id: "company", label: "Company / organisation", labelKey: "events.creationCreateEventRegistration.companyOrganisation", locked: false },
  { id: "title", label: "Job title", labelKey: "events.creationCreateEventRegistration.jobTitle", locked: false },
];
const MODES = ["required", "optional", "off"] as const;
type Mode = typeof MODES[number];

// Date helpers for window presets
function toDateTime(str: string, fallback: [number, number] = [9, 0]): Date {
  const { date, time } = parseDateString(str);
  const d = new Date(date);
  if (time && /^\d{1,2}:\d{2}$/.test(time)) { const [h, m] = time.split(":").map(Number); d.setHours(h, m, 0, 0); }
  else d.setHours(fallback[0], fallback[1], 0, 0);
  return d;
}
function fmtDT(d: Date): string { return formatDateString(d, `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`); }

// ── Generic picker modal chrome ───────────────────────────────────────────────
function PickerModal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  useEffect(() => { const k = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); }; document.addEventListener("keydown", k); return () => document.removeEventListener("keydown", k); }, [onClose]);
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,18,25,.42)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "min(560px, 100%)", maxHeight: "80vh", background: T.white, borderRadius: 14, boxShadow: "0 24px 60px rgba(15,18,25,.28)", display: "flex", flexDirection: "column", overflow: "hidden", fontFamily: T.font }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 18px", borderBottom: `1px solid ${T.border}` }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.n1 }}>{title}</div>
          <Btn onClick={onClose} style={{ border: "none", background: "transparent", color: T.n2, cursor: "pointer", display: "inline-flex", height: "auto", padding: 0 }}><Icon.x size={18} /></Btn>
        </div>
        {children}
      </div>
    </div>
  );
}
const pickRowStyle = { display: "flex", alignItems: "center", gap: 12, padding: 11, border: `1px solid ${T.border}`, borderRadius: 10, background: T.white, cursor: "pointer", fontFamily: T.font, width: "100%", textAlign: "left" as const, height: "auto", whiteSpace: "normal" as const };

// ── Booking questions (library picker + drag-to-reorder) ──────────────────────
type QItem = { kind: "question" | "group"; id: string };
function EventBookingQuestions() {
  const t = useT();
  const cfg = useCreationConfig();
  const qstore = useQuestions();
  const items: QItem[] = ((cfg as { bookingQuestions?: QItem[] }).bookingQuestions) || [];
  const setItems = (next: QItem[]) => CreationStore.setField("bookingQuestions", next);
  const [picking, setPicking] = useState(false);
  const [query, setQuery] = useState("");
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);

  const resolve = (it: QItem) => it.kind === "group" ? (qstore.groups().find((g) => g.id === it.id) || {}) : (qstore.questions().find((q) => q.id === it.id) || {});
  const add = (kind: "question" | "group", id: string) => { if (!items.some((x) => x.kind === kind && x.id === id)) setItems([...items, { kind, id }]); };
  const rm = (idx: number) => setItems(items.filter((_, i) => i !== idx));
  const taken = new Set(items.map((x) => x.kind + x.id));
  const qs = qstore.questions().filter((q) => !taken.has("question" + q.id) && (!query.trim() || (q.label || "").toLowerCase().includes(query.toLowerCase())));
  const gs = qstore.groups().filter((g) => !taken.has("group" + g.id) && (!query.trim() || (g.name || "").toLowerCase().includes(query.toLowerCase())));

  const onDrop = (target: number) => {
    if (dragIdx === null || dragIdx === target) { setDragIdx(null); setOverIdx(null); return; }
    const next = [...items];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(target, 0, moved);
    setItems(next); setDragIdx(null); setOverIdx(null);
  };

  return (
    <Card style={{ padding: 24 }}>
      <SubHead title={t("events.creationCreateEventRegistration.bookingQuestions", "Booking questions")} hint={t('events.creationCreateEventRegistration.hint')} />
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.length === 0 ? (
          <div style={{ padding: 16, background: T.n6, borderRadius: 8, fontSize: 13, color: T.soft }}>{t("events.creationCreateEventRegistration.noBookingQuestionsYetAddQuestionsOrGroupsFromYourLibraryBelow", "No booking questions yet. Add questions or groups from your library below.")}</div>
        ) : items.map((it, i) => {
          const obj = resolve(it) as { name?: string; label?: string; items?: unknown[] };
          const isGroup = it.kind === "group";
          return (
            <div key={it.kind + it.id} draggable onDragStart={() => setDragIdx(i)} onDragOver={(e) => { e.preventDefault(); setOverIdx(i); }} onDrop={() => onDrop(i)} onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 11px", border: `1px solid ${overIdx === i && dragIdx !== null ? T.p1 : T.border}`, borderRadius: 9, background: dragIdx === i ? T.p4 : T.white, opacity: dragIdx === i ? 0.6 : 1, cursor: "grab" }}>
              <Icon.grip size={15} style={{ color: T.n3, flexShrink: 0 }} />
              <span style={{ width: 26, height: 26, borderRadius: 6, background: isGroup ? T.p4 : T.n6, color: isGroup ? T.p1 : T.n2, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{isGroup ? <Icon.layers size={13} /> : <Icon.help size={13} />}</span>
              <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{obj.name || obj.label || "—"}</span>
              {isGroup ? <Pill tone="primary">{t("events.creationCreateEventRegistration.group", "Group ·")}{(obj.items || []).length}</Pill> : <Pill tone="neutral">{t("events.creationCreateEventRegistration.question", "Question")}</Pill>}
              <Btn onClick={() => rm(i)} title={t("common.actions.remove", "Remove")} style={{ border: "none", background: "transparent", color: T.n3, cursor: "pointer", display: "inline-flex", height: "auto", padding: 0 }}><Icon.x size={15} /></Btn>
            </div>
          );
        })}
        <div><Btn variant="secondary" size="sm" icon={<Icon.plus size={13} />} onClick={() => setPicking(true)}>{t("events.creationCreateEventRegistration.addQuestions", "Add questions")}</Btn></div>
      </div>
      {picking ? (
        <PickerModal title={t("events.creationCreateEventRegistration.addBookingQuestions", "Add booking questions")} onClose={() => setPicking(false)}>
          <div style={{ padding: "12px 16px 8px" }}>
            <Input full leading={<Icon.search size={14} />} size="sm" autoFocus placeholder={t("events.creationCreateEventRegistration.searchQuestionsGroups", "Search questions & groups…")} value={query} onChange={(e: ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)} />
          </div>
          <div style={{ flex: 1, overflow: "auto", padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
            {gs.map((g) => (
              <Btn key={"g" + g.id} onClick={() => add("group", g.id)} style={pickRowStyle}>
                <span style={{ width: 32, height: 32, borderRadius: 7, background: T.p4, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.layers size={15} /></span>
                <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 14, fontWeight: 600, color: T.n1 }}>{g.name}</div><div style={{ fontSize: 12, color: T.soft }}>{t("events.creationCreateEventRegistration.questionGroup", "Question group ·")}{t("events.creationCreateEventRegistration.questionCount.icu", { count: (g.items || []).length })}</div></div>
                <Icon.plus size={16} />
              </Btn>
            ))}
            {qs.map((q) => (
              <Btn key={"q" + q.id} onClick={() => add("question", q.id)} style={pickRowStyle}>
                <span style={{ width: 32, height: 32, borderRadius: 7, background: T.n6, color: T.n2, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.help size={15} /></span>
                <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 14, fontWeight: 600, color: T.n1 }}>{q.label}</div><div style={{ fontSize: 12, color: T.soft }}>{t("events.creationCreateEventRegistration.question", "Question")}</div></div>
                <Icon.plus size={16} />
              </Btn>
            ))}
            {gs.length + qs.length === 0 ? <div style={{ padding: 24, textAlign: "center", color: T.soft, fontSize: 13 }}>{query.trim() ? t('events.creationCreateEventRegistration.text') : t('events.creationCreateEventRegistration.text2')}</div> : null}
          </div>
        </PickerModal>
      ) : null}
    </Card>
  );
}

// ── Surveys (select one or more for this event) ───────────────────────────────
function EventSurveys() {
  const t = useT();
  const cfg = useCreationConfig();
  const { surveys } = useSurveys();
  const ids: string[] = ((cfg as { surveys?: string[] }).surveys) || [];
  const setIds = (next: string[]) => CreationStore.setField("surveys", next);
  const [picking, setPicking] = useState(false);
  const [query, setQuery] = useState("");
  const add = (id: string) => { if (!ids.includes(id)) setIds([...ids, id]); };
  const rm = (id: string) => setIds(ids.filter((x) => x !== id));
  const options = surveys.filter((s) => !ids.includes(s.id) && (!query.trim() || s.name.toLowerCase().includes(query.toLowerCase())));

  return (
    <Card style={{ padding: 24 }}>
      <SubHead title={t("events.creationCreateEventRegistration.surveys", "Surveys")} hint={t('events.creationCreateEventRegistration.hint2')} />
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {ids.length === 0 ? (
          <div style={{ padding: 16, background: T.n6, borderRadius: 8, fontSize: 13, color: T.soft }}>{t("events.creationCreateEventRegistration.noSurveysAttachedAddOneOrMoreSurveysToGatherFeedbackAfter", "No surveys attached. Add one or more surveys to gather feedback after the event.")}</div>
        ) : ids.map((id) => {
          const s = surveys.find((x) => x.id === id);
          return (
            <div key={id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 11px", border: `1px solid ${T.border}`, borderRadius: 9, background: T.white }}>
              <span style={{ width: 26, height: 26, borderRadius: 6, background: T.p4, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.csv size={13} /></span>
              <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s ? s.name : id}</span>
              {s ? <Pill tone="neutral">{(s.items || []).length} {t("events.creationCreateEventRegistration.question1f5087db", "question")}{(s.items || []).length === 1 ? "" : t('events.creationCreateEventRegistration.text3')}</Pill> : null}
              <Btn onClick={() => rm(id)} title={t("common.actions.remove", "Remove")} style={{ border: "none", background: "transparent", color: T.n3, cursor: "pointer", display: "inline-flex", height: "auto", padding: 0 }}><Icon.x size={15} /></Btn>
            </div>
          );
        })}
        <div><Btn variant="secondary" size="sm" icon={<Icon.plus size={13} />} onClick={() => setPicking(true)}>{t("events.creationCreateEventRegistration.addSurveys", "Add surveys")}</Btn></div>
      </div>
      {picking ? (
        <PickerModal title={t("events.creationCreateEventRegistration.addSurveys", "Add surveys")} onClose={() => setPicking(false)}>
          <div style={{ padding: "12px 16px 8px" }}>
            <Input full leading={<Icon.search size={14} />} size="sm" autoFocus placeholder={t("events.creationCreateEventRegistration.searchSurveys", "Search surveys…")} value={query} onChange={(e: ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)} />
          </div>
          <div style={{ flex: 1, overflow: "auto", padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
            {options.map((s) => (
              <Btn key={s.id} onClick={() => add(s.id)} style={pickRowStyle}>
                <span style={{ width: 32, height: 32, borderRadius: 7, background: T.p4, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.csv size={15} /></span>
                <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 14, fontWeight: 600, color: T.n1 }}>{s.name}</div><div style={{ fontSize: 12, color: T.soft }}>{t("events.creationCreateEventRegistration.questionCount.icu", { count: (s.items || []).length })}{s.description ? /* TODO(i18n): replace template-literal with t('events.creationCreateEventRegistration.text4', { ... }) */` · ${s.description}` : ""}</div></div>
                <Icon.plus size={16} />
              </Btn>
            ))}
            {options.length === 0 ? <div style={{ padding: 24, textAlign: "center", color: T.soft, fontSize: 13 }}>{query.trim() ? t('events.creationCreateEventRegistration.text5') : surveys.length ? t('events.creationCreateEventRegistration.text6') : t('events.creationCreateEventRegistration.text7')}</div> : null}
          </div>
        </PickerModal>
      ) : null}
    </Card>
  );
}

// ── CreateEventRegistration ───────────────────────────────────────────────────
export default function CreateEventRegistration() {
  const t = useT();
  const modeLabels: Record<Mode, string> = {
    required: t("settings.integrationDetail.required", "Required"),
    optional: t("events.creationCreateEventRegistration.optional", "optional"),
    off: t("settings.security.off", "Off"),
  };
  const cfg = useCreationConfig();
  const TODAY = fmtDT(new Date());

  const [openAt, setOpenAt] = useState((cfg as { regOpens?: string }).regOpens || TODAY);
  const [closeAt, setCloseAt] = useState((cfg as { regCloses?: string }).regCloses || "");
  const [openPreset, setOpenPreset] = useState<string | null>((cfg as { regOpenPreset?: string }).regOpenPreset || null);
  const [closePreset, setClosePreset] = useState<string | null>((cfg as { regClosePreset?: string }).regClosePreset || null);
  const [approvalMode, setApprovalMode] = useState((cfg.visibility || "").startsWith("Invite") ? "manual" : "auto");
  const [allowGuests, setAllowGuests] = useState(!!(cfg as { allowGuests?: boolean }).allowGuests);
  const [requireGuestDetails, setRequireGuestDetails] = useState(!!(cfg as { requireGuestDetails?: boolean }).requireGuestDetails);
  const [maxGuests, setMaxGuests] = useState<number>((cfg as { maxGuests?: number }).maxGuests ?? 1);
  const [collectMarketing, setCollectMarketing] = useState((cfg as { collectMarketing?: boolean }).collectMarketing !== false);
  const [collectShareConsent, setCollectShareConsent] = useState(!!(cfg as { collectShareConsent?: boolean }).collectShareConsent);
  const [confirmAction, setConfirmAction] = useState<"confirmation" | "redirect">((cfg as { confirmAction?: "confirmation" | "redirect" }).confirmAction || "confirmation");
  const [redirectUrl, setRedirectUrl] = useState((cfg as { redirectUrl?: string }).redirectUrl || "");
  const [termsText, setTermsText] = useState((cfg as { termsText?: string }).termsText || "By registering, you agree to share your details with the organiser for event management. We'll only contact you about this event unless you opt-in to marketing below.");
  // ui-config — field mode identifiers, not rendered labels.
  const [stdFields, setStdFields] = useState<Record<string, Mode>>({ firstName: "required", lastName: "required", email: "required", phone: "required", homeAddress: "optional", company: "optional", title: "optional", ...((cfg as { stdFields?: Record<string, Mode> }).stdFields || {}) });

  // Mirror to store
  useEffect(() => { CreationStore.setField("regOpens", openAt); CreationStore.setField("regCloses", closeAt); }, [openAt, closeAt]);
  useEffect(() => { CreationStore.setField("regOpenPreset", openPreset); CreationStore.setField("regClosePreset", closePreset); }, [openPreset, closePreset]);
  useEffect(() => { CreationStore.setField("stdFields", stdFields); }, [stdFields]);
  useEffect(() => {
    CreationStore.setField("allowGuests", allowGuests);
    CreationStore.setField("requireGuestDetails", requireGuestDetails);
    CreationStore.setField("maxGuests", maxGuests);
    CreationStore.setField("collectMarketing", collectMarketing);
    CreationStore.setField("collectShareConsent", collectShareConsent);
    CreationStore.setField("confirmAction", confirmAction);
    CreationStore.setField("redirectUrl", redirectUrl);
    CreationStore.setField("termsText", termsText);
  }, [allowGuests, requireGuestDetails, maxGuests, collectMarketing, collectShareConsent, confirmAction, redirectUrl, termsText]);

  const startDT = () => toDateTime((cfg.start as string) || "", [9, 0]);
  const endDT = () => toDateTime((cfg.end as string) || (cfg.start as string) || "", [17, 0]);

  const openPresets = [{ id: "open-now", label: t("events.creationCreateEventRegistration.openOnPublish", "Open on publish"), apply: () => setOpenAt(TODAY) }];
  const closePresets = [
    { id: "close-1d", label: t("events.creationCreateEventRegistration.text1dBefore", "1d before"), apply: () => setCloseAt(fmtDT(new Date(startDT().getTime() - 86400000))) },
    { id: "close-1h", label: t("events.creationCreateEventRegistration.text1hBefore", "1h before"), apply: () => setCloseAt(fmtDT(new Date(startDT().getTime() - 3600000))) },
    { id: "close-start", label: t("events.creationCreateEventRegistration.closeAtStart", "Close at start"), apply: () => setCloseAt(fmtDT(startDT())) },
    { id: "close-end", label: t("events.creationCreateEventRegistration.keepOpen", "Keep open"), apply: () => setCloseAt(fmtDT(endDT())) },
  ];

  return (
    <CreationShell step={4}>
      <WizardCanvas width="wide" cols="stack">
        {/* Registration window */}
        <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 0 }}>
          <SubHead title={t("events.creationCreateEventRegistration.registrationWindow", "Registration window")} hint={t('events.creationCreateEventRegistration.hint3')} />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, alignItems: "start" }}>
            <div>
              <DatePicker label={t("events.creationCreateEventRegistration.opens", "Opens")} value={openAt} onChange={(v) => { setOpenAt(v as string); setOpenPreset(null); }} showTime leading={<Icon.calendar size={14} />} />
              <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                {openPresets.map((p) => <PresetPill key={p.id} label={p.label} active={openPreset === p.id} onClick={() => { p.apply(); setOpenPreset(p.id); }} />)}
              </div>
            </div>
            <div>
              <DatePicker label={t("events.creationCreateEventRegistration.closes", "Closes")} value={closeAt} onChange={(v) => { setCloseAt(v as string); setClosePreset(null); }} showTime />
              <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                {closePresets.map((p) => <PresetPill key={p.id} label={p.label} active={closePreset === p.id} onClick={() => { p.apply(); setClosePreset(p.id); }} />)}
              </div>
            </div>
          </div>
        </Card>

        {/* Approval mode */}
        <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 0 }}>
          <SubHead title={t("events.creationCreateEventRegistration.registrationApproval", "Registration approval")} hint={t('events.creationCreateEventRegistration.hint4')} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {[{ v: "auto", l: "Auto-approve", d: "Registrations are confirmed instantly." }, { v: "manual", l: "Approve manually", d: "Each registration needs your sign-off." }].map((o) => {
              const on = approvalMode === o.v;
              return (
                <Btn key={o.v} type="button" onClick={() => setApprovalMode(o.v)} style={{ display: "flex", flexDirection: "column", gap: 3, padding: "12px 14px", border: `${on ? 2 : 1}px solid ${on ? T.p1 : T.n4}`, borderRadius: 8, background: on ? T.p4 : T.white, cursor: "pointer", fontFamily: T.font, textAlign: "left", height: "auto", whiteSpace: "normal" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: on ? T.p1 : T.n1, fontWeight: 600, fontSize: 13 }}>{on ? <Icon.check size={11} /> : null}{o.l}</span>
                  <span style={{ fontSize: 11, color: T.soft }}>{o.d}</span>
                </Btn>
              );
            })}
          </div>
        </Card>

        {/* +1 / guests */}
        <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 0 }}>
          <SubHead title={t("events.creationCreateEventRegistration.additionalGuests1s", "Additional guests (+1s)")} hint={t('events.creationCreateEventRegistration.hint5')} />
          <ToggleRow label={t("events.creationCreateEventRegistration.allowAdditionalGuests", "Allow additional guests")} sub={t('events.creationCreateEventRegistration.sub')} on={allowGuests} onChange={setAllowGuests} />
          {allowGuests ? (
            <>
              <ToggleRow label={t("events.creationCreateEventRegistration.require1Details", "Require +1 details")} sub={t('events.creationCreateEventRegistration.sub2')} on={requireGuestDetails} onChange={setRequireGuestDetails} />
              <div style={{ padding: "12px 0" }}>
                <label style={labelSt}>{t("events.creationCreateEventRegistration.maxGuestsPerRegistration", "Max guests per registration")}</label>
                <NumberField value={maxGuests} onChange={(n) => setMaxGuests(Math.max(1, n))} min={1} ariaLabel={t('events.creationCreateEventRegistration.ariaLabel')} style={{ width: 100 }} />
              </div>
            </>
          ) : null}
        </Card>

        {/* Standard attendee fields */}
        <Card style={{ padding: 24 }}>
          <SubHead title={t("events.creationCreateEventRegistration.standardAttendeeFields", "Standard attendee fields")} hint={t('events.creationCreateEventRegistration.hint6')} />
          <div style={{ display: "flex", flexDirection: "column" }}>
            {STD_FIELDS.map((f) => (
              <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${T.border}` }}>
                <span style={{ flex: 1, fontSize: 13, color: T.n1, fontWeight: 500 }}>{t(f.labelKey, f.label)}</span>
                {f.locked ? (
                  <span style={{ fontSize: 12, color: T.soft, display: "inline-flex", alignItems: "center", gap: 4 }}><Icon.lock size={11} /> {t("events.creationCreateEventRegistration.alwaysRequired", "Always required")}</span>
                ) : (
                  <div style={{ display: "inline-flex", borderRadius: 4, border: `1px solid ${T.n4}`, overflow: "hidden" }}>
                    {MODES.map((m) => {
                      const on = (stdFields[f.id] || "optional") === m;
                      return <Btn key={m} type="button" onClick={() => setStdFields((s) => ({ ...s, [f.id]: m }))} style={{ padding: "4px 10px", border: "none", background: on ? T.n1 : T.white, color: on ? T.white : T.n2, fontSize: 12, fontWeight: on ? 600 : 400, cursor: "pointer", fontFamily: T.font, textTransform: "capitalize", height: "auto", borderRadius: 0 }}>{modeLabels[m]}</Btn>;
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>

        {/* Booking questions — library picker with drag-to-reorder */}
        <EventBookingQuestions />

        {/* Surveys — select one or more post-event surveys */}
        <EventSurveys />

        {/* Consent & marketing */}
        <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 0 }}>
          <SubHead title={t("events.creationCreateEventRegistration.consentMarketing", "Consent & marketing")} hint={t('events.creationCreateEventRegistration.hint7')} />
          <ToggleRow label={t("events.creationCreateEventRegistration.requestMarketingOptIn", "Request marketing opt-in")} sub={t('events.creationCreateEventRegistration.sub3')} on={collectMarketing} onChange={setCollectMarketing} />
          <ToggleRow label={t("events.creationCreateEventRegistration.requestContactSharingConsent", "Request contact-sharing consent")} sub={t('events.creationCreateEventRegistration.sub4')} on={collectShareConsent} onChange={setCollectShareConsent} />
          <div style={{ paddingTop: 12 }}>
            <label style={labelSt}>{t("events.creationCreateEventRegistration.termsConditionsCopy", "Terms & conditions copy")}</label>
            <TermsRichField value={termsText} onChange={setTermsText} />
          </div>
        </Card>

        {/* After-registration action */}
        <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 0 }}>
          <SubHead title={t("events.creationCreateEventRegistration.afterRegistration", "After registration")} hint={t('events.creationCreateEventRegistration.hint8')} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {[{ v: "confirmation", l: "Show confirmation page", d: "JRNI-hosted page with all event details." }, { v: "redirect", l: "Redirect to a URL", d: "Send guests straight to your own page." }].map((o) => {
              const on = confirmAction === o.v;
              return (
                <Btn key={o.v} type="button" onClick={() => setConfirmAction(o.v as typeof confirmAction)} style={{ display: "flex", flexDirection: "column", gap: 3, padding: "12px 14px", border: `${on ? 2 : 1}px solid ${on ? T.p1 : T.n4}`, borderRadius: 8, background: on ? T.p4 : T.white, cursor: "pointer", fontFamily: T.font, textAlign: "left", height: "auto", whiteSpace: "normal" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: on ? T.p1 : T.n1, fontWeight: 600, fontSize: 13 }}>{on ? <Icon.check size={11} /> : null}{o.l}</span>
                  <span style={{ fontSize: 11, color: T.soft }}>{o.d}</span>
                </Btn>
              );
            })}
          </div>
          {confirmAction === "redirect" ? (
            <div style={{ marginTop: 12 }}>
              <label style={labelSt}>{t("events.creationCreateEventRegistration.redirectUrl", "Redirect URL")}</label>
              <UrlField
                value={redirectUrl}
                onChange={setRedirectUrl}
                placeholder={t("events.creationCreateEventRegistration.https", "https://…")}
                example="https://example.com/thanks"
                aria-label={t("events.creationCreateEventRegistration.redirectUrl", "Redirect URL")}
              />
            </div>
          ) : null}
        </Card>
      </WizardCanvas>
    </CreationShell>
  );
}
